import { type Car } from '@prisma/client'
import { useState } from 'react'
import { calculateDiscountedPrice } from '#app/shared/lib/price.ts'
import { PackOptimaWarningModal } from '#app/shared/ui/data-display/PackOptimaComponents.tsx'
import { Field } from '#app/shared/ui/fields.tsx'
import {
  type WarrantyDurationTypes,
  type WarrantyTypes,
} from '../../../../../../types/enums'
import { PACK_OPTIMA_MIN_MONTHS } from '../../usePackOptima'
import Warranty from '../warranty'

const numbers = [36, 48, 60, 72, 84, 96, 108, 120]
type Props = {
  warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }
  setWarrantyChoice: (warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }) => void
  activeNumber: number
  setActiveNumber: (num: number) => void
  initialEntry: number
  setInitialEntry: (initialEntry: number) => void
  car: Car
  hasPackOptima: boolean
  maxPackGamaEntry: number
}

export default function CardOptions({
  warrantyChoice,
  setWarrantyChoice,
  activeNumber,
  setActiveNumber,
  initialEntry,
  setInitialEntry,
  car,
  hasPackOptima,
  maxPackGamaEntry,
}: Props) {
  console.log('maxPackGamaEntry', maxPackGamaEntry)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<{
    type: 'months' | 'entry'
    value: number
  } | null>(null)

  // Calculate the maximum initial entry (50% of the vehicle's financing value)
  const financedPrice =
    calculateDiscountedPrice(car.minFinancedAmount ?? 0, car.discount) ?? 0
  const maxInitialEntry = Math.floor(financedPrice * 0.9)

  const handleNumberClick = (number: number) => {
    // Check if user is switching from >=72 to <72 months y tenía Pack Óptima
    if (hasPackOptima && number < PACK_OPTIMA_MIN_MONTHS) {
      setPendingAction({ type: 'months', value: number })
      setShowWarningModal(true)
    } else {
      setActiveNumber(number)
    }
  }

  const handleKeepPackOptima = () => {
    setShowWarningModal(false)
    setPendingAction(null)
  }

  const handleContinueWithoutBenefits = () => {
    if (pendingAction?.type === 'months') {
      setActiveNumber(pendingAction.value)
    } else if (pendingAction?.type === 'entry') {
      setInitialEntry(pendingAction.value)
    }
    setShowWarningModal(false)
    setPendingAction(null)
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`
          flex flex-col items-center gap-6

          lg:flex-col lg:items-start lg:gap-6

          md:flex-row md:gap-2

          xl:flex-row xl:items-center xl:gap-2
        `}
      >
        <div
          className={`
            relative flex w-full flex-col

            md:w-[280px]
          `}
        >
          <Field
            labelProps={{
              children: 'Entrada Inicial',
              className: 'text-subtitle1-bold text-neutral-900',
            }}
            inputProps={{
              placeholder: 'Entrada Inicial',
              variant: 'outlined',
              rightIcon: '€',
              defaultValue: initialEntry,
              min: 0,
              max: maxInitialEntry,
              type: 'number',
              className:
                'h-14 rounded-lg border border-neutral-100 bg-white px-1 pr-4 pb-[2px]',
              value: initialEntry,
              onChange: (e) => {
                const value = Number(e.target.value)

                // If has Pack Optima and the new value would lose it (lowers from 50%), show modal
                if (hasPackOptima && value > maxPackGamaEntry) {
                  setPendingAction({
                    type: 'entry',
                    value: Math.min(value, maxInitialEntry),
                  })
                  setShowWarningModal(true)
                  // No update the state yet, wait for the modal response
                  return
                }

                setInitialEntry(Math.min(value, maxInitialEntry))
              },
            }}
            inputClassName="text-right text-base-medium"
            className="flex flex-col space-y-3 text-neutral-500"
          />
          <p className="absolute bottom-0 top-20 mt-1 text-xs text-neutral-400">
            No puede superar al 90% del precio.
          </p>
        </div>

        <div className="flex w-full flex-col space-y-3">
          <p className={`text-subtitle1-bold text-neutral-900`}>
            Plazo (en meses)
          </p>

          <div
            className={`
              z-[1] flex max-w-full flex-grow flex-wrap items-center gap-1

              md:flex-nowrap
            `}
          >
            {numbers.map((number) => (
              <button
                type="button"
                key={number}
                className={`
                  flex h-14 min-h-14 w-14 min-w-14 items-center justify-center
                  rounded-lg bg-white

                  ${
                    activeNumber === number
                      ? 'border-2 border-primary-500'
                      : `border border-neutral-100`
                  }
                `}
                onClick={() => handleNumberClick(number)}
              >
                {number}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Warranty
        warrantyChoice={warrantyChoice}
        setWarrantyChoice={setWarrantyChoice}
        car={car as any}
        activeNumber={activeNumber}
        hasPackOptima={hasPackOptima}
      />

      <PackOptimaWarningModal
        isOpen={showWarningModal}
        onKeepPackOptima={handleKeepPackOptima}
        onContinueWithoutBenefits={handleContinueWithoutBenefits}
        discountAmount={
          (calculateDiscountedPrice(
            car.minFinancedAmount ?? 0,
            car.discount ?? 0,
          ) ?? 0) - car.price
        }
      />
    </div>
  )
}
